import { useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material'
import RefreshIcon from '@mui/icons-material/Refresh'
import MoneyOffIcon from '@mui/icons-material/MoneyOff'
import GavelIcon from '@mui/icons-material/Gavel'
import EditIcon from '@mui/icons-material/Edit'
import BlockIcon from '@mui/icons-material/Block'
import VisibilityIcon from '@mui/icons-material/Visibility'
import VolunteerActivismIcon from '@mui/icons-material/VolunteerActivism'
import {
  AccountDto,
  AdminPenaltyTransactionDto,
  PENALTY_REASONS,
  PenaltyAdjustmentMode,
  PenaltyReason,
  PenaltySearchParams,
  PenaltyTransactionStatus,
  PenaltyTransactionType,
  accountService,
  penaltyAdminService,
} from '../api/adminServices'
import { useToast } from '../components/useToast'
import AccountDetailDialog from '../components/AccountDetailDialog'
import { EntityAccountCell } from '../components/EntityAccountCell'
import { formatCurrency } from '../utils/formatCurrency'
import { useLocalizedLabel } from '../utils/getLocalizedLabel'

/**
 * Penalties & Compensations — the System Admin's tools for resolving a customer complaint about a penalty
 * (docs/design/penalty-mechanism.md "Admin resolution" in the backend repo).
 *
 * Every row is one side of an incident: the CHARGE on the party at fault, the COMPENSATION to the injured
 * party. An admin can cancel (waive) either side, re-price it by amount or by a percentage of what was
 * originally assessed, or grant a goodwill compensation no incident priced. By default both sides of an
 * incident move together; every action requires a reason and is kept in the row's history.
 *
 * The pricing itself — ladders, percentages, the compensation share — lives in System Configuration →
 * Penalties.
 */

const STATUSES: PenaltyTransactionStatus[] = ['PENDING', 'PROCESSING', 'COMPLETED', 'FAILED', 'CANCELLED']
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

const statusColor = (s: PenaltyTransactionStatus): 'default' | 'success' | 'warning' | 'error' | 'info' => {
  switch (s) {
    case 'COMPLETED':
      return 'success'
    case 'FAILED':
      return 'error'
    case 'PENDING':
    case 'PROCESSING':
      return 'warning'
    default:
      return 'default'
  }
}

const shortId = (id?: string | null) => (id ? id.slice(0, 8) : '—')
/** he-IL currency strings carry bidi marks; isolate each so "a → b" keeps its order in an LTR line. */
const Money = ({ v, c }: { v?: number | null; c: string }) => <bdi>{formatCurrency(v, c)}</bdi>
const fmtDate = (iso?: string | null) => (iso ? new Date(iso).toLocaleString() : '—')
const isActionable = (t: AdminPenaltyTransactionDto) =>
  t.status !== 'CANCELLED' && t.status !== 'PROCESSING' && !t.heldAt
/** A charge its party may still contest: not cancelled, not billed, never contested, inside its window. */
const isContestable = (t: AdminPenaltyTransactionDto) =>
  t.transactionType === 'CHARGE' &&
  !t.adminCreated &&
  t.status !== 'CANCELLED' &&
  !t.contestedAt &&
  !t.disputeId &&
  !t.billedAt &&
  !!t.contestableUntil &&
  new Date(t.contestableUntil).getTime() > Date.now()

/**
 * Where the row stands with the monthly billing cycle: held by an open contest, billed, or waiting for the next
 * invoice (and, for a charge still inside its window, contestable until when).
 */
function BillingChip({ t, onOpenDispute }: { t: AdminPenaltyTransactionDto; onOpenDispute?: (id: string) => void }) {
  if (t.status === 'CANCELLED') return null
  if (t.heldAt) {
    return (
      <Tooltip
        title={
          t.disputeId
            ? 'A penalty contest is open: neither side is billed until the dispute is decided.'
            : 'Contested: neither side is billed until the dispute is decided. The dispute case is being opened.'
        }
      >
        <Chip
          size="small"
          color="warning"
          label="Under dispute"
          onClick={t.disputeId && onOpenDispute ? () => onOpenDispute(t.disputeId as string) : undefined}
        />
      </Tooltip>
    )
  }
  if (t.rescueId && !t.rescueResolvedAt) {
    return (
      <Tooltip title="The Freighter dropped this work and the cargo is being re-placed. Owed only if no replacement is matched — in full, or for the part that was not; not billed until then.">
        <Chip size="small" color="secondary" variant="outlined" label="Depends on re-match" />
      </Tooltip>
    )
  }
  if (t.billedAt) {
    return (
      <Tooltip title={t.invoiceId ? `Invoice ${t.invoiceId}` : 'Billing is off in the pilot: recorded, not charged.'}>
        <Chip size="small" variant="outlined" label={`Billed ${new Date(t.billedAt).toLocaleDateString()}`} />
      </Tooltip>
    )
  }
  if (isContestable(t)) {
    return (
      <Chip
        size="small"
        variant="outlined"
        color="info"
        label={`Contestable until ${new Date(t.contestableUntil as string).toLocaleString()}`}
      />
    )
  }
  return <Chip size="small" variant="outlined" label="Next invoice" />
}
const assessedOf = (t: AdminPenaltyTransactionDto) => t.originalAmount ?? t.amount

/** Round to cents in the given direction — mirrors PenaltyAdminService so the preview is what will be saved. */
const roundCents = (v: number, dir: 'down' | 'up' | 'half') => {
  const cents = v * 100
  const r = dir === 'down' ? Math.floor(cents + 1e-9) : dir === 'up' ? Math.ceil(cents - 1e-9) : Math.round(cents)
  return r / 100
}

function newAmountFor(t: AdminPenaltyTransactionDto, mode: PenaltyAdjustmentMode, value: number): number {
  if (mode === 'AMOUNT') return roundCents(value, 'half')
  // Toward the account holder: down on a charge, up on a compensation.
  return roundCents((assessedOf(t) * value) / 100, t.transactionType === 'CHARGE' ? 'down' : 'up')
}

function counterpartPreview(
  counterpart: AdminPenaltyTransactionDto,
  target: AdminPenaltyTransactionDto,
  newAmount: number,
): number {
  const ratio = newAmount / assessedOf(target)
  const projected = roundCents(assessedOf(counterpart) * ratio, counterpart.transactionType === 'CHARGE' ? 'up' : 'down')
  return Math.min(projected, assessedOf(counterpart))
}

function AmountCell({ t }: { t: AdminPenaltyTransactionDto }) {
  const changed = t.originalAmount != null && t.originalAmount !== t.amount
  return (
    <Box>
      <Typography
        variant="body2"
        sx={{
          fontWeight: 600,
          textDecoration: t.status === 'CANCELLED' ? 'line-through' : undefined,
          color: t.transactionType === 'CHARGE' ? 'error.main' : 'success.main',
        }}
      >
        {t.transactionType === 'CHARGE' ? '−' : '+'}
        {formatCurrency(t.amount, t.currency)}
      </Typography>
      {changed && (
        <Typography variant="caption" color="text.secondary" sx={{ textDecoration: 'line-through' }}>
          {formatCurrency(t.originalAmount, t.currency)}
        </Typography>
      )}
    </Box>
  )
}

/** What each incident means, in words — the enum label alone does not tell an admin what happened. */
const REASON_DESCRIPTIONS: Record<string, string> = {
  NO_SHOW: 'The freighter did not show up at pickup',
  CANCEL_JOB: 'The freighter cancelled the job',
  CANCEL_SHIPMENT: 'The shipper cancelled after a freighter had committed',
  LATE_PICKUP: 'Late pickup (reserved — no longer raised)',
  LATE_DELIVERY: 'Delivered after the promised dropoff window',
  CARGO_NOT_READY: 'The cargo was not ready at pickup',
  FREIGHTER_FAULT_RETURN: "Cargo returned through the freighter's fault",
  OTHER: 'Other — set by an admin',
}

/** The account behind a row: clickable name (opens the account, as on other pages), its type, and its role. */
function PartyCell({
  t,
  name,
  accountType,
  onOpenAccount,
}: {
  t: AdminPenaltyTransactionDto
  name: string
  accountType?: string | null
  onOpenAccount: (accountId: string) => void
}) {
  return (
    <Stack gap={0.5} alignItems="flex-start">
      <EntityAccountCell accountId={t.accountId} display={name} tooltip={t.accountId} onOpenAccount={onOpenAccount} />
      <Stack direction="row" gap={0.5} alignItems="center" flexWrap="wrap">
        {accountType && (
          <Chip
            size="small"
            color={accountType === 'FREIGHTER' ? 'warning' : 'primary'}
            label={accountType === 'FREIGHTER' ? 'Freighter' : accountType === 'SHIPPER' ? 'Shipper' : accountType}
            sx={{ height: 20, fontSize: 11 }}
          />
        )}
        <Typography variant="caption" color="text.secondary">
          {t.transactionType === 'CHARGE' ? 'at fault' : 'injured party'}
        </Typography>
      </Stack>
    </Stack>
  )
}

/** Why the row exists, and — when an admin changed it — why it was changed. */
function ReasonCell({ t, label }: { t: AdminPenaltyTransactionDto; label: string }) {
  return (
    <Box>
      <Typography variant="body2" sx={{ fontWeight: 600 }}>
        {label}
      </Typography>
      <Typography variant="caption" display="block" color="text.secondary">
        {t.adminCreated ? 'Goodwill compensation granted by an admin' : REASON_DESCRIPTIONS[t.penaltyReason] ?? ''}
      </Typography>
      {t.lastAdminReason && (
        <Tooltip title={t.lastAdminReason}>
          <Typography
            variant="caption"
            display="block"
            color="warning.main"
            sx={{ maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
          >
            Admin: {t.lastAdminReason}
            {t.lastAdminReferenceId ? ` (ref: ${t.lastAdminReferenceId})` : ''}
          </Typography>
        </Tooltip>
      )}
    </Box>
  )
}

function AdminChip({ t }: { t: AdminPenaltyTransactionDto }) {
  if (t.adminCreated) return <Chip size="small" color="secondary" variant="outlined" label="Goodwill" />
  if (t.adminAction === 'CANCEL') return <Chip size="small" variant="outlined" label="Cancelled by admin" />
  if (t.adminAction === 'ADJUST') return <Chip size="small" color="info" variant="outlined" label="Adjusted" />
  return null
}

export default function PenaltiesPage() {
  const toast = useToast()
  const queryClient = useQueryClient()
  const localized = useLocalizedLabel()
  const reasonLabel = (r: string) => {
    const l = localized('PenaltyType', r)
    return l && l !== r ? l : r.charAt(0) + r.slice(1).toLowerCase().replace(/_/g, ' ')
  }
  const [searchParams] = useSearchParams()

  // ---- filters ---------------------------------------------------------
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(25)
  const [accountId, setAccountId] = useState<string | null>(searchParams.get('accountId'))
  const [idQuery, setIdQuery] = useState(searchParams.get('jobId') ?? '')
  const [transactionType, setTransactionType] = useState<PenaltyTransactionType | ''>('')
  const [penaltyReason, setPenaltyReason] = useState<PenaltyReason | ''>('')
  const [status, setStatus] = useState<PenaltyTransactionStatus | ''>('')
  const [adminTouched, setAdminTouched] = useState(false)
  const [fromDate, setFromDate] = useState('')
  const [toDate, setToDate] = useState('')

  const trimmedId = idQuery.trim()
  const idValid = trimmedId === '' || UUID_RE.test(trimmedId)

  const params: PenaltySearchParams = {
    page,
    size: rowsPerPage,
    accountId: accountId ?? undefined,
    // A pasted id is tried as a job id first — most complaints arrive quoting the job.
    jobId: idValid && trimmedId ? trimmedId : undefined,
    transactionType: transactionType || undefined,
    penaltyReason: penaltyReason || undefined,
    status: status || undefined,
    adminTouched: adminTouched || undefined,
    from: fromDate ? `${fromDate}T00:00:00` : undefined,
    to: toDate ? `${toDate}T23:59:59` : undefined,
  }

  const listQuery = useQuery({
    queryKey: ['admin-penalties', params],
    queryFn: () => penaltyAdminService.search(params),
    enabled: idValid,
  })

  const accountsQuery = useQuery({
    queryKey: ['accounts-for-penalties'],
    queryFn: () => accountService.getAll(0, 500),
  })
  const accounts: AccountDto[] = useMemo(() => accountsQuery.data?.content ?? [], [accountsQuery.data])
  const accountById = useMemo(() => {
    const map = new Map<string, AccountDto>()
    accounts.forEach((a) => map.set(a.id, a))
    return map
  }, [accounts])
  const accountName = (id?: string | null) => {
    if (!id) return '—'
    const a = accountById.get(id)
    return a ? a.businessName || a.name : shortId(id)
  }
  const accountTypeOf = (t: AdminPenaltyTransactionDto) => t.accountType ?? accountById.get(t.accountId)?.type ?? null
  const [accountDetailId, setAccountDetailId] = useState<string | null>(null)
  const party = (t: AdminPenaltyTransactionDto) => (
    <PartyCell
      t={t}
      name={accountName(t.accountId)}
      accountType={accountTypeOf(t)}
      onOpenAccount={setAccountDetailId}
    />
  )

  // ---- detail + actions -----------------------------------------------
  const [detailId, setDetailId] = useState<string | null>(null)
  const detailQuery = useQuery({
    queryKey: ['admin-penalty-detail', detailId],
    queryFn: () => penaltyAdminService.getById(detailId as string),
    enabled: !!detailId,
  })

  const navigate = useNavigate()
  const openDispute = (disputeId: string) => navigate(`/disputes?disputeId=${disputeId}`)
  const [contestTarget, setContestTarget] = useState<AdminPenaltyTransactionDto | null>(null)
  const [adjustTarget, setAdjustTarget] = useState<AdminPenaltyTransactionDto | null>(null)
  const [cancelTarget, setCancelTarget] = useState<AdminPenaltyTransactionDto | null>(null)
  const [goodwillOpen, setGoodwillOpen] = useState(false)

  /** Actions need the counterpart, which only the detail endpoint returns. */
  const openAction = async (t: AdminPenaltyTransactionDto, kind: 'adjust' | 'cancel') => {
    try {
      const full = t.counterparts ? t : await queryClient.fetchQuery({
        queryKey: ['admin-penalty-detail', t.id],
        queryFn: () => penaltyAdminService.getById(t.id),
      })
      if (kind === 'adjust') setAdjustTarget(full)
      else setCancelTarget(full)
    } catch (e) {
      toast.errorFrom(e, 'Could not load the transaction')
    }
  }

  const refreshAll = () => {
    queryClient.invalidateQueries({ queryKey: ['admin-penalties'] })
    queryClient.invalidateQueries({ queryKey: ['admin-penalty-detail'] })
  }

  const rows = listQuery.data?.content ?? []

  return (
    <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
          <Box>
            <Stack direction="row" alignItems="center" gap={1}>
              <MoneyOffIcon color="primary" />
              <Typography variant="h6">Penalties &amp; Compensations</Typography>
            </Stack>
            <Typography variant="body2" color="text.secondary">
              Resolve complaints: cancel or re-price a charge or a compensation, or grant a goodwill compensation.
              Both sides of an incident move together by default, and every change is recorded with its reason.
            </Typography>
          </Box>
          <Stack direction="row" gap={1}>
            <Button variant="contained" startIcon={<VolunteerActivismIcon />} onClick={() => setGoodwillOpen(true)}>
              Goodwill compensation
            </Button>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={() => listQuery.refetch()}
              disabled={listQuery.isFetching}
            >
              Refresh
            </Button>
          </Stack>
        </Stack>

        <Grid container spacing={1.5} sx={{ mt: 1 }}>
          <Grid item xs={12} md={3}>
            <Autocomplete
              size="small"
              options={accounts}
              loading={accountsQuery.isLoading}
              value={accountId ? accountById.get(accountId) ?? null : null}
              getOptionLabel={(a) => `${a.businessName || a.name} (${a.type})`}
              isOptionEqualToValue={(a, b) => a.id === b.id}
              onChange={(_, a) => {
                setAccountId(a?.id ?? null)
                setPage(0)
              }}
              renderInput={(p) => <TextField {...p} label="Account" />}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              size="small"
              fullWidth
              label="Job ID"
              value={idQuery}
              error={!idValid}
              helperText={!idValid ? 'Paste a full job id' : undefined}
              onChange={(e) => {
                setIdQuery(e.target.value)
                setPage(0)
              }}
            />
          </Grid>
          <Grid item xs={6} md={2}>
            <FormControl size="small" fullWidth>
              <InputLabel>Type</InputLabel>
              <Select
                label="Type"
                value={transactionType}
                onChange={(e) => {
                  setTransactionType(e.target.value as PenaltyTransactionType | '')
                  setPage(0)
                }}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="CHARGE">Charges</MenuItem>
                <MenuItem value="COMPENSATION">Compensations</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} md={2}>
            <FormControl size="small" fullWidth>
              <InputLabel>Reason</InputLabel>
              <Select
                label="Reason"
                value={penaltyReason}
                onChange={(e) => {
                  setPenaltyReason(e.target.value as PenaltyReason | '')
                  setPage(0)
                }}
              >
                <MenuItem value="">All</MenuItem>
                {PENALTY_REASONS.map((r) => (
                  <MenuItem key={r} value={r}>
                    {reasonLabel(r)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} md={2}>
            <FormControl size="small" fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                label="Status"
                value={status}
                onChange={(e) => {
                  setStatus(e.target.value as PenaltyTransactionStatus | '')
                  setPage(0)
                }}
              >
                <MenuItem value="">All</MenuItem>
                {STATUSES.map((s) => (
                  <MenuItem key={s} value={s}>
                    {s}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} md={2}>
            <TextField
              size="small"
              fullWidth
              type="date"
              label="From"
              InputLabelProps={{ shrink: true }}
              value={fromDate}
              onChange={(e) => {
                setFromDate(e.target.value)
                setPage(0)
              }}
            />
          </Grid>
          <Grid item xs={6} md={2}>
            <TextField
              size="small"
              fullWidth
              type="date"
              label="To"
              InputLabelProps={{ shrink: true }}
              value={toDate}
              onChange={(e) => {
                setToDate(e.target.value)
                setPage(0)
              }}
            />
          </Grid>
          <Grid item xs={12} md={4} sx={{ display: 'flex', alignItems: 'center' }}>
            <FormControlLabel
              control={
                <Switch
                  checked={adminTouched}
                  onChange={(e) => {
                    setAdminTouched(e.target.checked)
                    setPage(0)
                  }}
                />
              }
              label="Only rows changed by an admin"
            />
          </Grid>
        </Grid>
      </Paper>

      {listQuery.isError && <Alert severity="error" sx={{ mb: 2 }}>Could not load penalty transactions.</Alert>}

      {listQuery.isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
          <CircularProgress />
        </Box>
      ) : rows.length === 0 ? (
        <Paper sx={{ p: 6, textAlign: 'center' }}>
          <Typography color="text.secondary">No penalty transactions match these filters.</Typography>
        </Paper>
      ) : (
        <Paper sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          <TableContainer sx={{ flex: 1, overflow: 'auto' }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Created</TableCell>
                  <TableCell>Account</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Reason</TableCell>
                  <TableCell>Amount</TableCell>
                  <TableCell>Platform fee</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Job / shipment</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((t) => (
                  <TableRow key={t.id} hover sx={{ cursor: 'pointer' }} onClick={() => setDetailId(t.id)}>
                    <TableCell>{fmtDate(t.createdAt)}</TableCell>
                    <TableCell sx={{ minWidth: 170 }} onClick={(e) => e.stopPropagation()}>
                      {party(t)}
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        color={t.transactionType === 'CHARGE' ? 'error' : 'success'}
                        variant="outlined"
                        label={t.transactionType === 'CHARGE' ? 'Charge' : 'Compensation'}
                      />
                    </TableCell>
                    <TableCell>
                      <ReasonCell t={t} label={reasonLabel(t.penaltyReason)} />
                    </TableCell>
                    <TableCell>
                      <AmountCell t={t} />
                    </TableCell>
                    <TableCell>
                      {t.platformFeeAmount != null ? formatCurrency(t.platformFeeAmount, t.currency) : '—'}
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" gap={0.5} flexWrap="wrap" sx={{ maxWidth: 230 }}>
                        <Chip size="small" color={statusColor(t.status)} label={t.status} />
                        <AdminChip t={t} />
                        <BillingChip t={t} onOpenDispute={openDispute} />
                      </Stack>
                    </TableCell>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>
                      {shortId(t.jobId)} / {shortId(t.shipmentId)}
                    </TableCell>
                    <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                      <Tooltip title="Details & history">
                        <IconButton size="small" onClick={() => setDetailId(t.id)}>
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t.heldAt ? 'Under dispute — decide it in the dispute' : 'Change amount'}>
                        <span>
                          <IconButton size="small" disabled={!isActionable(t)} onClick={() => openAction(t, 'adjust')}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip
                        title={
                          t.heldAt
                            ? 'Under dispute — decide it in the dispute'
                            : 'Cancel incident (nobody pays, nobody is paid)'
                        }
                      >
                        <span>
                          <IconButton
                            size="small"
                            color="error"
                            // A cancelled row stays clickable: its incident may still have an open side to close.
                            disabled={t.status === 'PROCESSING' || !!t.heldAt}
                            onClick={() => openAction(t, 'cancel')}
                          >
                            <BlockIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={listQuery.data?.totalElements ?? 0}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10))
              setPage(0)
            }}
            rowsPerPageOptions={[10, 25, 50, 100]}
          />
        </Paper>
      )}

      <DetailDialog
        open={!!detailId}
        loading={detailQuery.isLoading}
        transaction={detailQuery.data ?? null}
        party={party}
        reasonLabel={reasonLabel}
        onClose={() => setDetailId(null)}
        onAdjust={(t) => setAdjustTarget(t)}
        onCancel={(t) => setCancelTarget(t)}
        onContest={(t) => setContestTarget(t)}
        onOpenDispute={openDispute}
      />

      {contestTarget && (
        <ContestDialog
          target={contestTarget}
          accountName={accountName}
          onClose={() => setContestTarget(null)}
          onDone={() => {
            setContestTarget(null)
            refreshAll()
            toast.success('Contested — the penalty and its compensation are on hold; the dispute case opens shortly')
          }}
        />
      )}

      {adjustTarget && (
        <AdjustDialog
          target={adjustTarget}
          accountName={accountName}
          onClose={() => setAdjustTarget(null)}
          onDone={() => {
            setAdjustTarget(null)
            refreshAll()
            toast.success('Amount updated')
          }}
        />
      )}

      {cancelTarget && (
        <CancelDialog
          target={cancelTarget}
          accountName={accountName}
          onClose={() => setCancelTarget(null)}
          onDone={() => {
            setCancelTarget(null)
            refreshAll()
            toast.success('Cancelled')
          }}
        />
      )}

      {goodwillOpen && (
        <GoodwillDialog
          accounts={accounts}
          defaultAccountId={accountId}
          reasonLabel={reasonLabel}
          onClose={() => setGoodwillOpen(false)}
          onDone={(t) => {
            setGoodwillOpen(false)
            refreshAll()
            setDetailId(t.id)
            toast.success('Goodwill compensation granted')
          }}
        />
      )}
      <AccountDetailDialog
        accountId={accountDetailId}
        open={!!accountDetailId}
        onClose={() => setAccountDetailId(null)}
      />
    </Box>
  )
}

// ---------------------------------------------------------------------------
// Detail
// ---------------------------------------------------------------------------

function DetailDialog({
  open,
  loading,
  transaction: t,
  party,
  reasonLabel,
  onClose,
  onAdjust,
  onCancel,
  onContest,
  onOpenDispute,
}: {
  open: boolean
  loading: boolean
  transaction: AdminPenaltyTransactionDto | null
  party: (t: AdminPenaltyTransactionDto) => React.ReactNode
  reasonLabel: (r: string) => string
  onClose: () => void
  onAdjust: (t: AdminPenaltyTransactionDto) => void
  onCancel: (t: AdminPenaltyTransactionDto) => void
  onContest: (t: AdminPenaltyTransactionDto) => void
  onOpenDispute: (disputeId: string) => void
}) {
  const rowsById = useMemo(() => {
    const map = new Map<string, AdminPenaltyTransactionDto>()
    if (t) {
      map.set(t.id, t)
      ;(t.counterparts ?? []).forEach((c) => map.set(c.id, c))
    }
    return map
  }, [t])

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>Penalty transaction</DialogTitle>
      <DialogContent dividers>
        {loading || !t ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Stack gap={2}>
            <Grid container spacing={2}>
              <Field label="Account" value={party(t)} />
              <Field label="Type" value={t.transactionType === 'CHARGE' ? 'Charge (debit)' : 'Compensation (credit)'} />
              <Field label="Reason" value={<ReasonCell t={t} label={reasonLabel(t.penaltyReason)} />} />
              <Field label="Status" value={<Chip size="small" color={statusColor(t.status)} label={t.status} />} />
              <Field label="Amount" value={<AmountCell t={t} />} />
              <Field
                label="Platform fee"
                value={t.platformFeeAmount != null ? formatCurrency(t.platformFeeAmount, t.currency) : '—'}
              />
              <Field label="Job" value={t.jobId ?? '—'} mono />
              <Field label="Shipment" value={t.shipmentId ?? '—'} mono />
              <Field label="Created" value={fmtDate(t.createdAt)} />
              <Field label="Processed" value={fmtDate(t.processedAt)} />
              <Field label="Billing" value={<BillingChip t={t} onOpenDispute={onOpenDispute} />} />
              {t.transactionType === 'CHARGE' && (
                <Field label="Contestable until" value={fmtDate(t.contestableUntil)} />
              )}
              {t.stripePaymentIntentId && <Field label="Stripe payment" value={t.stripePaymentIntentId} mono />}
              {t.stripeRefundId && <Field label="Last Stripe refund" value={t.stripeRefundId} mono />}
              {t.failureReason && <Field label="Failure" value={t.failureReason} />}
            </Grid>

            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Other side of this incident
              </Typography>
              {t.adminCreated ? (
                <Typography variant="body2" color="text.secondary">
                  A goodwill compensation granted by an admin — not paired with an incident.
                </Typography>
              ) : (t.counterparts ?? []).length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  None recorded.
                </Typography>
              ) : (
                (t.counterparts ?? []).map((c) => (
                  <Stack key={c.id} direction="row" gap={2} alignItems="center" sx={{ py: 0.5 }}>
                    <Chip
                      size="small"
                      variant="outlined"
                      color={c.transactionType === 'CHARGE' ? 'error' : 'success'}
                      label={c.transactionType === 'CHARGE' ? 'Charge' : 'Compensation'}
                    />
                    {party(c)}
                    <AmountCell t={c} />
                    <Chip size="small" color={statusColor(c.status)} label={c.status} />
                  </Stack>
                ))
              )}
            </Box>

            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Admin history
              </Typography>
              {(t.adjustments ?? []).length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  Untouched — exactly as the incident raised it.
                </Typography>
              ) : (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>When</TableCell>
                      <TableCell>Row</TableCell>
                      <TableCell>Action</TableCell>
                      <TableCell>Change</TableCell>
                      <TableCell>Reason</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(t.adjustments ?? []).map((a) => {
                      const row = rowsById.get(a.penaltyTransactionId)
                      const currency = row?.currency ?? t.currency
                      return (
                        <TableRow key={a.id}>
                          <TableCell>{fmtDate(a.createdAt)}</TableCell>
                          <TableCell>
                            {row ? (row.transactionType === 'CHARGE' ? 'Charge' : 'Compensation') : shortId(a.penaltyTransactionId)}
                            {a.cascadedFromId && (
                              <Typography variant="caption" display="block" color="text.secondary">
                                cascaded
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>{a.action}</TableCell>
                          <TableCell>
                            {a.previousAmount != null && a.newAmount != null && a.previousAmount !== a.newAmount ? (
                              <>
                                <Money v={a.previousAmount} c={currency} /> → <Money v={a.newAmount} c={currency} />
                              </>
                            ) : a.previousAmount == null && a.newAmount != null ? (
                              <Money v={a.newAmount} c={currency} />
                            ) : null}
                            {a.previousStatus !== a.newStatus && (
                              <Typography variant="caption" display="block">
                                {a.previousStatus ?? 'new'} → {a.newStatus}
                              </Typography>
                            )}
                            {a.previousPlatformFeeAmount !== a.newPlatformFeeAmount && a.newPlatformFeeAmount != null && (
                              <Typography variant="caption" display="block" color="text.secondary">
                                fee <Money v={a.previousPlatformFeeAmount} c={currency} /> →{' '}
                                <Money v={a.newPlatformFeeAmount} c={currency} />
                              </Typography>
                            )}
                            {a.stripeRefundId && (
                              <Typography variant="caption" display="block" color="text.secondary">
                                refund {a.stripeRefundId}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            {a.reason}
                            {a.referenceId && (
                              <Typography variant="caption" display="block" color="text.secondary">
                                ref: {a.referenceId}
                              </Typography>
                            )}
                            {!a.accountNotified && (
                              <Typography variant="caption" display="block" color="text.secondary">
                                account not notified
                              </Typography>
                            )}
                          </TableCell>
                        </TableRow>
                      )
                    })}
                  </TableBody>
                </Table>
              )}
            </Box>

            {t.notes && (
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Notes
                </Typography>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }} color="text.secondary">
                  {t.notes}
                </Typography>
              </Box>
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        {t?.disputeId && (
          <Button startIcon={<GavelIcon />} onClick={() => onOpenDispute(t.disputeId as string)}>
            {t.heldAt ? 'Decide in the dispute' : 'Open the dispute'}
          </Button>
        )}
        {t && isContestable(t) && (
          <Tooltip title="Open a penalty contest on the charged party's behalf (e.g. a phone complaint)">
            <Button startIcon={<GavelIcon />} onClick={() => onContest(t)}>
              Contest on their behalf
            </Button>
          </Tooltip>
        )}
        {t && isActionable(t) && (
          <Button startIcon={<EditIcon />} onClick={() => onAdjust(t)}>
            Change amount
          </Button>
        )}
        {t && !t.heldAt && [t, ...(t.counterparts ?? [])].some((r) => r.status !== 'CANCELLED') && (
          <Button color="error" startIcon={<BlockIcon />} onClick={() => onCancel(t)}>
            Cancel incident
          </Button>
        )}
        <Box sx={{ flex: 1 }} />
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  )
}

function Field({ label, value, mono }: { label: string; value: React.ReactNode; mono?: boolean }) {
  return (
    <Grid item xs={12} sm={6} md={4}>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Box sx={{ fontFamily: mono ? 'monospace' : undefined, fontSize: mono ? 12 : undefined, wordBreak: 'break-all' }}>
        {value}
      </Box>
    </Grid>
  )
}

// ---------------------------------------------------------------------------
// Shared bits of the action dialogs
// ---------------------------------------------------------------------------

function ResolutionFields({
  reason,
  setReason,
  referenceId,
  setReferenceId,
}: {
  reason: string
  setReason: (v: string) => void
  referenceId: string
  setReferenceId: (v: string) => void
}) {
  return (
    <>
      <TextField
        label="Reason (required)"
        helperText="Kept in the audit trail. Summarise the complaint and the decision."
        fullWidth
        multiline
        minRows={2}
        required
        value={reason}
        inputProps={{ maxLength: 2000 }}
        onChange={(e) => setReason(e.target.value)}
      />
      <TextField
        label="Reference"
        helperText="Optional: support ticket, dispute id, call note…"
        fullWidth
        value={referenceId}
        inputProps={{ maxLength: 255 }}
        onChange={(e) => setReferenceId(e.target.value)}
      />
    </>
  )
}

function activeCounterparts(t: AdminPenaltyTransactionDto) {
  return (t.counterparts ?? []).filter((c) => c.status !== 'CANCELLED')
}

// ---------------------------------------------------------------------------
// Adjust
// ---------------------------------------------------------------------------

function AdjustDialog({
  target,
  accountName,
  onClose,
  onDone,
}: {
  target: AdminPenaltyTransactionDto
  accountName: (id?: string | null) => string
  onClose: () => void
  onDone: () => void
}) {
  const toast = useToast()
  const [mode, setMode] = useState<PenaltyAdjustmentMode>('PERCENTAGE')
  const [value, setValue] = useState('')
  const [reason, setReason] = useState('')
  const [referenceId, setReferenceId] = useState('')
  const [cascade, setCascade] = useState(true)
  const [notify, setNotify] = useState(true)

  const assessed = assessedOf(target)
  const numeric = parseFloat(value)
  const hasValue = value.trim() !== '' && !isNaN(numeric)
  const newAmount = hasValue ? newAmountFor(target, mode, numeric) : null
  const collected = target.transactionType === 'CHARGE' && !!target.stripePaymentIntentId && target.status === 'COMPLETED'
  const counterparts = activeCounterparts(target)

  const error = (() => {
    if (!hasValue || newAmount == null) return null
    if (mode === 'PERCENTAGE' && (numeric <= 0 || numeric > 100)) return 'Between 0 and 100% of the original assessment.'
    if (newAmount <= 0) return 'Must be above zero — to remove it entirely, cancel the transaction instead.'
    if (newAmount > assessed) return `Cannot exceed the original ${formatCurrency(assessed, target.currency)}. To pay more, grant a goodwill compensation.`
    if (newAmount === target.amount) return 'That is the current amount.'
    if (collected && newAmount > target.amount) return 'This charge was already collected; it can only be lowered.'
    return null
  })()

  const mutation = useMutation({
    mutationFn: () =>
      penaltyAdminService.adjust(target.id, {
        mode,
        value: numeric,
        reason: reason.trim(),
        referenceId: referenceId.trim() || undefined,
        cascadeToCounterpart: cascade,
        notifyAccount: notify,
      }),
    onSuccess: onDone,
    onError: (e) => toast.errorFrom(e, 'Could not change the amount'),
  })

  const canSubmit = hasValue && !error && reason.trim().length > 0 && !mutation.isPending

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Change {target.transactionType === 'CHARGE' ? 'charge' : 'compensation'} amount</DialogTitle>
      <DialogContent>
        <Stack gap={2} sx={{ pt: 1 }}>
          <DialogContentText>
            {accountName(target.accountId)} — currently {formatCurrency(target.amount, target.currency)}
            {target.originalAmount != null && target.originalAmount !== target.amount
              ? `, originally ${formatCurrency(target.originalAmount, target.currency)}`
              : ''}
            .
          </DialogContentText>

          <ToggleButtonGroup
            exclusive
            size="small"
            value={mode}
            onChange={(_, m) => m && setMode(m)}
          >
            <ToggleButton value="PERCENTAGE">% of original</ToggleButton>
            <ToggleButton value="AMOUNT">New amount</ToggleButton>
          </ToggleButtonGroup>

          <TextField
            label={mode === 'PERCENTAGE' ? 'Percentage of the original assessment' : 'New amount'}
            type="number"
            autoFocus
            value={value}
            onChange={(e) => setValue(e.target.value)}
            error={!!error}
            helperText={
              error ??
              (newAmount != null
                ? `New amount: ${formatCurrency(newAmount, target.currency)}`
                : `Up to ${formatCurrency(assessed, target.currency)} (the original assessment)`)
            }
            inputProps={{ min: 0, step: mode === 'PERCENTAGE' ? 1 : 0.01, max: mode === 'PERCENTAGE' ? 100 : assessed }}
            InputProps={{
              endAdornment: <InputAdornment position="end">{mode === 'PERCENTAGE' ? '%' : target.currency}</InputAdornment>,
            }}
          />

          {counterparts.length > 0 && (
            <Box>
              <FormControlLabel
                control={<Switch checked={cascade} onChange={(e) => setCascade(e.target.checked)} />}
                label={`Move the ${target.transactionType === 'CHARGE' ? 'compensation' : 'charge'} by the same proportion`}
              />
              {counterparts.map((c) => (
                <Typography key={c.id} variant="caption" display="block" color="text.secondary" sx={{ ml: 6 }}>
                  {accountName(c.accountId)}: <Money v={c.amount} c={c.currency} />
                  {cascade && newAmount != null && !error ? (
                    <>
                      {' → '}
                      <Money v={counterpartPreview(c, target, newAmount)} c={c.currency} />
                    </>
                  ) : (
                    ' (unchanged)'
                  )}
                </Typography>
              ))}
              {!cascade && target.transactionType === 'CHARGE' && (
                <Alert severity="warning" sx={{ mt: 1 }}>
                  The compensation stays as it is — the platform covers any shortfall out of its fee.
                </Alert>
              )}
            </Box>
          )}

          {collected && newAmount != null && !error && newAmount < target.amount && (
            <Alert severity="info">
              This charge was collected through Stripe. {formatCurrency(target.amount - newAmount, target.currency)} will be
              refunded to the account.
            </Alert>
          )}

          <ResolutionFields reason={reason} setReason={setReason} referenceId={referenceId} setReferenceId={setReferenceId} />
          <FormControlLabel
            control={<Switch checked={notify} onChange={(e) => setNotify(e.target.checked)} />}
            label="Send push & email to the affected parties (account users, and the driver on a freighter-fault charge)"
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={mutation.isPending}>
          Back
        </Button>
        <Button variant="contained" disabled={!canSubmit} onClick={() => mutation.mutate()}>
          {mutation.isPending ? 'Saving…' : 'Change amount'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

// ---------------------------------------------------------------------------
// Cancel
// ---------------------------------------------------------------------------

const QUICK_REASONS = [
  'Resolved between the parties outside the platform (arbitration)',
  'Raised in error',
  'Documented force majeure (accident, road closure, weather)',
]

/**
 * Cancel — by default the whole incident: the charge and the compensation it funds, so nobody pays and nobody
 * is paid. The admin does not need to know which row is which; it works from either side. Cancelling only
 * this side is the exception, behind a switch.
 */
function CancelDialog({
  target,
  accountName,
  onClose,
  onDone,
}: {
  target: AdminPenaltyTransactionDto
  accountName: (id?: string | null) => string
  onClose: () => void
  onDone: () => void
}) {
  const toast = useToast()
  const [reason, setReason] = useState('')
  const [referenceId, setReferenceId] = useState('')
  const [onlyThisSide, setOnlyThisSide] = useState(false)
  const [notify, setNotify] = useState(true)

  const isCharge = target.transactionType === 'CHARGE'
  const incident = [target, ...(target.counterparts ?? [])].sort((a, b) =>
    a.transactionType === b.transactionType ? 0 : a.transactionType === 'CHARGE' ? -1 : 1,
  )
  const open = incident.filter((t) => t.status !== 'CANCELLED')
  const affected = onlyThisSide ? open.filter((t) => t.id === target.id) : open
  const canSplit = target.status !== 'CANCELLED' && open.length > 1
  const refunded = affected.filter(
    (t) => t.transactionType === 'CHARGE' && !!t.stripePaymentIntentId && t.status === 'COMPLETED',
  )

  const mutation = useMutation({
    mutationFn: () => {
      const request = {
        reason: reason.trim(),
        referenceId: referenceId.trim() || undefined,
        notifyAccount: notify,
      }
      return onlyThisSide
        ? penaltyAdminService.cancel(target.id, { ...request, cascadeToCounterpart: false })
        : penaltyAdminService.cancelIncident(target.id, request)
    },
    onSuccess: onDone,
    onError: (e) => toast.errorFrom(e, 'Could not cancel'),
  })

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{onlyThisSide ? `Cancel only this ${isCharge ? 'charge' : 'compensation'}?` : 'Cancel this incident?'}</DialogTitle>
      <DialogContent>
        <Stack gap={2} sx={{ pt: 1 }}>
          {open.length === 0 ? (
            <Alert severity="info">Everything in this incident is already cancelled.</Alert>
          ) : (
            <>
              <DialogContentText>
                {onlyThisSide
                  ? 'Only the row below is cancelled; the other side of the incident stays as it is.'
                  : 'Nobody pays and nobody is paid: every open row of this incident is cancelled and drops out of both Finance Reports.'}
              </DialogContentText>
              <Table size="small">
                <TableBody>
                  {incident.map((t) => {
                    const hit = affected.some((a) => a.id === t.id)
                    return (
                      <TableRow key={t.id}>
                        <TableCell>{accountName(t.accountId)}</TableCell>
                        <TableCell>{t.transactionType === 'CHARGE' ? 'would pay' : 'would be paid'}</TableCell>
                        <TableCell align="right">
                          <Typography
                            variant="body2"
                            sx={{ textDecoration: hit || t.status === 'CANCELLED' ? 'line-through' : undefined }}
                          >
                            <Money v={t.amount} c={t.currency} />
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {t.status === 'CANCELLED' ? (
                            <Chip size="small" label="already cancelled" />
                          ) : hit ? (
                            <Chip size="small" color="error" variant="outlined" label="→ cancelled" />
                          ) : (
                            <Chip size="small" color="warning" variant="outlined" label="stays" />
                          )}
                        </TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>

              {canSplit && (
                <Box>
                  <FormControlLabel
                    control={<Switch checked={onlyThisSide} onChange={(e) => setOnlyThisSide(e.target.checked)} />}
                    label={`Cancel only this ${isCharge ? 'charge' : 'compensation'}`}
                  />
                  {onlyThisSide && isCharge && (
                    <Alert severity="warning" sx={{ mt: 1 }}>
                      The compensation stays owed — the platform pays it out of its own pocket.
                    </Alert>
                  )}
                </Box>
              )}

              {refunded.map((t) => (
                <Alert key={t.id} severity="info">
                  {accountName(t.accountId)} was already charged through Stripe; <Money v={t.amount} c={t.currency} /> will be
                  refunded.
                </Alert>
              ))}

              <Stack direction="row" gap={1} flexWrap="wrap">
                {QUICK_REASONS.map((q) => (
                  <Chip key={q} label={q} size="small" variant="outlined" onClick={() => setReason(q)} />
                ))}
              </Stack>
              <ResolutionFields reason={reason} setReason={setReason} referenceId={referenceId} setReferenceId={setReferenceId} />
              <FormControlLabel
                control={<Switch checked={notify} onChange={(e) => setNotify(e.target.checked)} />}
                label="Send push & email to the affected parties (account users, and the driver on a freighter-fault charge)"
              />
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={mutation.isPending}>
          Back
        </Button>
        {open.length > 0 && (
          <Button
            variant="contained"
            color="error"
            disabled={!reason.trim() || mutation.isPending}
            onClick={() => mutation.mutate()}
          >
            {mutation.isPending ? 'Cancelling…' : onlyThisSide ? 'Cancel this side' : 'Cancel incident'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  )
}

// ---------------------------------------------------------------------------
// Contest on the charged party's behalf
// ---------------------------------------------------------------------------

function ContestDialog({
  target,
  accountName,
  onClose,
  onDone,
}: {
  target: AdminPenaltyTransactionDto
  accountName: (id?: string | null) => string
  onClose: () => void
  onDone: () => void
}) {
  const toast = useToast()
  const [comments, setComments] = useState('')
  const mutation = useMutation({
    mutationFn: () => penaltyAdminService.contest(target.id, comments.trim()),
    onSuccess: () => onDone(),
    onError: (e) => toast.errorFrom(e, 'Could not open the contest'),
  })

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Contest this penalty on the charged party's behalf?</DialogTitle>
      <DialogContent>
        <Stack gap={2} sx={{ pt: 1 }}>
          <DialogContentText>
            Opens a penalty contest for {accountName(target.accountId)} on the{' '}
            <Money v={target.amount} c={target.currency} /> charge. Until the dispute is decided, the charge and the
            compensation it funds are held out of billing, and both parties are notified.
          </DialogContentText>
          <TextField
            label="The charged party's case (required)"
            helperText="What they told you: what happened and why the penalty is wrong."
            fullWidth
            multiline
            minRows={3}
            value={comments}
            inputProps={{ maxLength: 2000 }}
            onChange={(e) => setComments(e.target.value)}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={mutation.isPending}>
          Back
        </Button>
        <Button variant="contained" disabled={!comments.trim() || mutation.isPending} onClick={() => mutation.mutate()}>
          {mutation.isPending ? 'Opening…' : 'Open contest'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

// ---------------------------------------------------------------------------
// Goodwill compensation
// ---------------------------------------------------------------------------

function GoodwillDialog({
  accounts,
  defaultAccountId,
  reasonLabel,
  onClose,
  onDone,
}: {
  accounts: AccountDto[]
  defaultAccountId: string | null
  reasonLabel: (r: string) => string
  onClose: () => void
  onDone: (t: AdminPenaltyTransactionDto) => void
}) {
  const toast = useToast()
  const [account, setAccount] = useState<AccountDto | null>(
    defaultAccountId ? accounts.find((a) => a.id === defaultAccountId) ?? null : null,
  )
  const [amount, setAmount] = useState('')
  const [penaltyReason, setPenaltyReason] = useState<PenaltyReason>('OTHER')
  const [jobId, setJobId] = useState('')
  const [shipmentId, setShipmentId] = useState('')
  const [reason, setReason] = useState('')
  const [referenceId, setReferenceId] = useState('')
  const [notify, setNotify] = useState(true)

  const numeric = parseFloat(amount)
  const amountValid = !isNaN(numeric) && numeric > 0
  const jobValid = jobId.trim() === '' || UUID_RE.test(jobId.trim())
  const shipmentValid = shipmentId.trim() === '' || UUID_RE.test(shipmentId.trim())

  const mutation = useMutation({
    mutationFn: () =>
      penaltyAdminService.grantCompensation({
        accountId: (account as AccountDto).id,
        amount: numeric,
        currency: account?.defaultCurrency || 'ILS',
        penaltyReason,
        jobId: jobId.trim() || undefined,
        shipmentId: shipmentId.trim() || undefined,
        reason: reason.trim(),
        referenceId: referenceId.trim() || undefined,
        notifyAccount: notify,
      }),
    onSuccess: onDone,
    onError: (e) => toast.errorFrom(e, 'Could not grant the compensation'),
  })

  const canSubmit = !!account && amountValid && jobValid && shipmentValid && !!reason.trim() && !mutation.isPending

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Grant a goodwill compensation</DialogTitle>
      <DialogContent>
        <Stack gap={2} sx={{ pt: 1 }}>
          <DialogContentText>
            For a complaint the penalty mechanism does not cover. It appears in the account's Finance Report as a
            compensation, recorded as owed like any other. To change an existing compensation, adjust it instead.
          </DialogContentText>
          <Autocomplete
            options={accounts}
            value={account}
            getOptionLabel={(a) => `${a.businessName || a.name} (${a.type})`}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            onChange={(_, a) => setAccount(a)}
            renderInput={(p) => <TextField {...p} label="Account" required />}
          />
          <TextField
            label="Amount"
            type="number"
            required
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            error={amount !== '' && !amountValid}
            inputProps={{ min: 0.01, step: 0.01 }}
            InputProps={{ endAdornment: <InputAdornment position="end">{account?.defaultCurrency || 'ILS'}</InputAdornment> }}
          />
          <FormControl fullWidth>
            <InputLabel>Related incident type</InputLabel>
            <Select
              label="Related incident type"
              value={penaltyReason}
              onChange={(e) => setPenaltyReason(e.target.value as PenaltyReason)}
            >
              {PENALTY_REASONS.map((r) => (
                <MenuItem key={r} value={r}>
                  {reasonLabel(r)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Stack direction={{ xs: 'column', sm: 'row' }} gap={2}>
            <TextField
              label="Job ID (optional)"
              fullWidth
              value={jobId}
              error={!jobValid}
              onChange={(e) => setJobId(e.target.value)}
            />
            <TextField
              label="Shipment ID (optional)"
              fullWidth
              value={shipmentId}
              error={!shipmentValid}
              onChange={(e) => setShipmentId(e.target.value)}
            />
          </Stack>
          <Divider />
          <ResolutionFields reason={reason} setReason={setReason} referenceId={referenceId} setReferenceId={setReferenceId} />
          <FormControlLabel
            control={<Switch checked={notify} onChange={(e) => setNotify(e.target.checked)} />}
            label="Send push & email to the account users"
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={mutation.isPending}>
          Back
        </Button>
        <Button variant="contained" disabled={!canSubmit} onClick={() => mutation.mutate()}>
          {mutation.isPending ? 'Granting…' : 'Grant compensation'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
